/**
 * Prompt Templates Management Page
 *
 * Super Admin management for the AI Prompt Template Library.
 * Allows creating, editing, and organizing categories and prompt templates
 * that power the AI Document Wizard in the user-tenant portal.
 */
import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  BookOpen,
  Plus,
  Search,
  RefreshCw,
  Edit2,
  Trash2,
  ChevronRight,
  FileText,
  Loader2,
  AlertCircle,
  Check,
  GripVertical,
  Tag,
  BarChart2,
} from 'lucide-react';
import { cn, Button, Input } from '@respectly/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@respectly/ui';
import { useAuth } from '@respectly/utils/auth';

const API_URL = import.meta.env.VITE_API_URL ?? '';

// ─── Types ────────────────────────────────────────────────

interface PromptTemplateItem {
  id: string;
  title: string;
  description: string | null;
  promptText: string;
  icon: string | null;
  sortOrder: number;
  isActive: boolean;
  usageCount: number;
  categoryId: string;
  category?: { name: string; slug: string; color: string };
}

interface PromptTemplateCategoryWithCount {
  id: string;
  name: string;
  slug: string;
  icon: string;
  color: string;
  sortOrder: number;
  isActive: boolean;
  _count: { templates: number };
}

// ─── Color options ────────────────────────────────────────

const COLOR_OPTIONS = [
  { value: 'blue',    label: 'Blue',    cls: 'bg-blue-500'    },
  { value: 'emerald', label: 'Emerald', cls: 'bg-emerald-500' },
  { value: 'red',     label: 'Red',     cls: 'bg-red-500'     },
  { value: 'violet',  label: 'Violet',  cls: 'bg-violet-500'  },
  { value: 'amber',   label: 'Amber',   cls: 'bg-amber-500'   },
  { value: 'slate',   label: 'Slate',   cls: 'bg-slate-500'   },
  { value: 'yellow',  label: 'Yellow',  cls: 'bg-yellow-500'  },
  { value: 'indigo',  label: 'Indigo',  cls: 'bg-indigo-500'  },
  { value: 'teal',    label: 'Teal',    cls: 'bg-teal-500'    },
  { value: 'rose',    label: 'Rose',    cls: 'bg-rose-500'    },
];

const categoryColorMap: Record<string, { bg: string; text: string }> = {
  blue:    { bg: 'bg-blue-100',    text: 'text-blue-700'    },
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  red:     { bg: 'bg-red-100',     text: 'text-red-700'     },
  violet:  { bg: 'bg-violet-100',  text: 'text-violet-700'  },
  amber:   { bg: 'bg-amber-100',   text: 'text-amber-700'   },
  slate:   { bg: 'bg-slate-100',   text: 'text-slate-700'   },
  yellow:  { bg: 'bg-yellow-100',  text: 'text-yellow-700'  },
  indigo:  { bg: 'bg-indigo-100',  text: 'text-indigo-700'  },
  teal:    { bg: 'bg-teal-100',    text: 'text-teal-700'    },
  rose:    { bg: 'bg-rose-100',    text: 'text-rose-700'    },
};

// ─── Animations ──────────────────────────────────────────

const listVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.04 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 28 } },
};

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════

export function PromptTemplates() {
  const { getAccessToken } = useAuth();

  // ─── State ──────────────────────────────────────────
  const [categories, setCategories] = useState<PromptTemplateCategoryWithCount[]>([]);
  const [templates, setTemplates] = useState<PromptTemplateItem[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [templateSearch, setTemplateSearch] = useState('');
  const [loadingCats, setLoadingCats] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Category modal
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [editingCat, setEditingCat] = useState<PromptTemplateCategoryWithCount | null>(null);
  const [catForm, setCatForm] = useState({ name: '', slug: '', icon: 'file-text', color: 'indigo' });
  const [catSaving, setCatSaving] = useState(false);
  const [catError, setCatError] = useState<string | null>(null);

  // Template modal
  const [tmplModalOpen, setTmplModalOpen] = useState(false);
  const [editingTmpl, setEditingTmpl] = useState<PromptTemplateItem | null>(null);
  const [tmplForm, setTmplForm] = useState({ title: '', description: '', promptText: '', icon: '', sortOrder: 0 });
  const [tmplSaving, setTmplSaving] = useState(false);
  const [tmplError, setTmplError] = useState<string | null>(null);

  // ─── API helper ─────────────────────────────────────
  const apiFetch = useCallback(async (path: string, options?: RequestInit) => {
    const token = await getAccessToken();
    const res = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options?.headers || {}),
      },
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error?.message || 'Request failed');
    return data;
  }, [getAccessToken]);

  // ─── Load categories ─────────────────────────────────
  const loadCategories = useCallback(async () => {
    setLoadingCats(true);
    setError(null);
    try {
      const data = await apiFetch('/api/super-admin/prompt-templates/categories');
      setCategories(data.data.categories);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load categories');
    } finally {
      setLoadingCats(false);
    }
  }, [apiFetch]);

  // ─── Load templates for selected category ────────────
  const loadTemplates = useCallback(async () => {
    if (!selectedCategoryId) { setTemplates([]); return; }
    setLoadingTemplates(true);
    try {
      const params = new URLSearchParams({ categoryId: selectedCategoryId, includeInactive: 'true', limit: '100' });
      if (templateSearch.trim()) params.set('search', templateSearch.trim());
      const data = await apiFetch(`/api/super-admin/prompt-templates?${params}`);
      setTemplates(data.data.templates);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load templates');
    } finally {
      setLoadingTemplates(false);
    }
  }, [apiFetch, selectedCategoryId, templateSearch]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  // ─── Category CRUD ───────────────────────────────────
  const openCreateCat = () => {
    setEditingCat(null);
    setCatForm({ name: '', slug: '', icon: 'file-text', color: 'indigo' });
    setCatError(null);
    setCatModalOpen(true);
  };

  const openEditCat = (cat: PromptTemplateCategoryWithCount) => {
    setEditingCat(cat);
    setCatForm({ name: cat.name, slug: cat.slug, icon: cat.icon, color: cat.color });
    setCatError(null);
    setCatModalOpen(true);
  };

  const saveCat = async () => {
    if (!catForm.name.trim() || !catForm.slug.trim()) {
      setCatError('Name and slug are required');
      return;
    }
    setCatSaving(true);
    setCatError(null);
    try {
      if (editingCat) {
        await apiFetch(`/api/super-admin/prompt-templates/categories/${editingCat.id}`, {
          method: 'PATCH',
          body: JSON.stringify(catForm),
        });
      } else {
        await apiFetch('/api/super-admin/prompt-templates/categories', {
          method: 'POST',
          body: JSON.stringify(catForm),
        });
      }
      setCatModalOpen(false);
      await loadCategories();
    } catch (e) {
      setCatError(e instanceof Error ? e.message : 'Failed to save category');
    } finally {
      setCatSaving(false);
    }
  };

  const deleteCat = async (cat: PromptTemplateCategoryWithCount) => {
    if (!confirm(`Delete category "${cat.name}"? This will fail if it has active templates.`)) return;
    try {
      await apiFetch(`/api/super-admin/prompt-templates/categories/${cat.id}`, { method: 'DELETE' });
      if (selectedCategoryId === cat.id) setSelectedCategoryId(null);
      await loadCategories();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to delete category');
    }
  };

  const toggleCatActive = async (cat: PromptTemplateCategoryWithCount) => {
    try {
      await apiFetch(`/api/super-admin/prompt-templates/categories/${cat.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !cat.isActive }),
      });
      await loadCategories();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to update category');
    }
  };

  // ─── Template CRUD ───────────────────────────────────
  const openCreateTmpl = () => {
    setEditingTmpl(null);
    setTmplForm({ title: '', description: '', promptText: '', icon: '', sortOrder: templates.length });
    setTmplError(null);
    setTmplModalOpen(true);
  };

  const openEditTmpl = (t: PromptTemplateItem) => {
    setEditingTmpl(t);
    setTmplForm({ title: t.title, description: t.description || '', promptText: t.promptText, icon: t.icon || '', sortOrder: t.sortOrder });
    setTmplError(null);
    setTmplModalOpen(true);
  };

  const saveTmpl = async () => {
    if (!tmplForm.title.trim()) { setTmplError('Title is required'); return; }
    if (!tmplForm.promptText.trim() || tmplForm.promptText.length < 10) { setTmplError('Prompt text must be at least 10 characters'); return; }
    setTmplSaving(true);
    setTmplError(null);
    try {
      if (editingTmpl) {
        await apiFetch(`/api/super-admin/prompt-templates/${editingTmpl.id}`, {
          method: 'PATCH',
          body: JSON.stringify(tmplForm),
        });
      } else {
        await apiFetch('/api/super-admin/prompt-templates', {
          method: 'POST',
          body: JSON.stringify({ ...tmplForm, categoryId: selectedCategoryId }),
        });
      }
      setTmplModalOpen(false);
      await loadTemplates();
    } catch (e) {
      setTmplError(e instanceof Error ? e.message : 'Failed to save template');
    } finally {
      setTmplSaving(false);
    }
  };

  const deleteTmpl = async (t: PromptTemplateItem) => {
    if (!confirm(`Deactivate template "${t.title}"?`)) return;
    try {
      await apiFetch(`/api/super-admin/prompt-templates/${t.id}`, { method: 'DELETE' });
      await loadTemplates();
      await loadCategories();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to delete template');
    }
  };

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);

  // ─── Render ──────────────────────────────────────────
  return (
    <div className="flex h-full flex-col">
      {/* Page header */}
      <div className="border-b border-slate-200 bg-white px-8 py-6 dark:border-slate-700 dark:bg-slate-800">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-100 dark:bg-indigo-900/50">
              <BookOpen className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">AI Prompt Templates</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Manage the template library shown in the AI Document Wizard
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadCategories} disabled={loadingCats}>
              <RefreshCw className={cn('h-4 w-4 mr-1.5', loadingCats && 'animate-spin')} />
              Refresh
            </Button>
            <Button size="sm" onClick={openCreateCat}>
              <Plus className="h-4 w-4 mr-1.5" />
              Add Category
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-8 mt-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-400">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Split-panel layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* ─── Left: Category list ─────────────────── */}
        <div className="flex w-72 shrink-0 flex-col border-r border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900">
          <div className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Categories ({categories.length})
          </div>

          {loadingCats ? (
            <div className="space-y-2 px-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-14 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
              ))}
            </div>
          ) : (
            <motion.div
              variants={listVariants}
              initial="hidden"
              animate="visible"
              className="flex-1 overflow-y-auto px-3 pb-4 space-y-1"
            >
              {categories.map((cat) => {
                const colors = (categoryColorMap[cat.color] ?? categoryColorMap['indigo'])!;
                const isSelected = selectedCategoryId === cat.id;
                return (
                  <motion.div
                    key={cat.id}
                    variants={itemVariants}
                    className={cn(
                      'group relative flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 transition-all',
                      isSelected
                        ? 'bg-white shadow-sm ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-600'
                        : 'hover:bg-white/70 dark:hover:bg-slate-800/70',
                      !cat.isActive && 'opacity-50',
                    )}
                    onClick={() => setSelectedCategoryId(isSelected ? null : cat.id)}
                  >
                    <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', colors.bg)}>
                      <Tag className={cn('h-4 w-4', colors.text)} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">
                        {cat.name}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {cat._count.templates} template{cat._count.templates !== 1 ? 's' : ''}
                      </p>
                    </div>
                    {isSelected && <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
                    {/* Hover actions */}
                    <div className="absolute right-2 top-1/2 hidden -translate-y-1/2 items-center gap-1 group-hover:flex">
                      <button
                        onClick={(e) => { e.stopPropagation(); openEditCat(cat); }}
                        className="flex h-6 w-6 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                        title="Edit category"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleCatActive(cat); }}
                        className="flex h-6 w-6 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                        title={cat.isActive ? 'Deactivate' : 'Activate'}
                      >
                        <Check className={cn('h-3.5 w-3.5', cat.isActive && 'text-emerald-500')} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteCat(cat); }}
                        className="flex h-6 w-6 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-950/30"
                        title="Delete category"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </motion.div>
                );
              })}

              {categories.length === 0 && (
                <div className="py-8 text-center text-sm text-slate-400">
                  No categories yet.{' '}
                  <button onClick={openCreateCat} className="text-indigo-600 hover:underline dark:text-indigo-400">
                    Add one
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </div>

        {/* ─── Right: Template list ─────────────────── */}
        <div className="flex flex-1 flex-col overflow-hidden bg-white dark:bg-slate-900">
          {selectedCategory ? (
            <>
              {/* Template list header */}
              <div className="flex items-center gap-4 border-b border-slate-200 px-6 py-4 dark:border-slate-700">
                <div className="flex-1">
                  <h2 className="font-semibold text-slate-900 dark:text-slate-100">{selectedCategory.name}</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {selectedCategory._count.templates} active template{selectedCategory._count.templates !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="relative w-56">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    placeholder="Search templates..."
                    className="pl-9"
                  />
                </div>
                <Button size="sm" onClick={openCreateTmpl}>
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add Template
                </Button>
              </div>

              {/* Template grid */}
              {loadingTemplates ? (
                <div className="grid grid-cols-2 gap-3 p-6 xl:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="h-28 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800" />
                  ))}
                </div>
              ) : templates.length > 0 ? (
                <motion.div
                  variants={listVariants}
                  initial="hidden"
                  animate="visible"
                  className="flex-1 overflow-y-auto p-6"
                >
                  <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
                    {templates.map((t) => {
                      const colors = (categoryColorMap[selectedCategory.color] ?? categoryColorMap['indigo'])!;
                      return (
                        <motion.div
                          key={t.id}
                          variants={itemVariants}
                          className={cn(
                            'group relative flex flex-col gap-2 rounded-xl border p-4 transition-all',
                            t.isActive
                              ? 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm dark:border-slate-700 dark:bg-slate-800'
                              : 'border-dashed border-slate-200 bg-slate-50 opacity-60 dark:border-slate-700 dark:bg-slate-900',
                          )}
                        >
                          <div className="flex items-start gap-3">
                            <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', colors.bg)}>
                              <FileText className={cn('h-4 w-4', colors.text)} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold leading-tight text-slate-800 dark:text-slate-200">
                                {t.title}
                              </p>
                              {t.description && (
                                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
                                  {t.description}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Usage count */}
                          <div className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
                            <BarChart2 className="h-3 w-3" />
                            {t.usageCount} use{t.usageCount !== 1 ? 's' : ''}
                            {!t.isActive && (
                              <span className="ml-auto rounded bg-slate-100 px-1.5 py-0.5 text-slate-500 dark:bg-slate-700">
                                Inactive
                              </span>
                            )}
                          </div>

                          {/* Prompt preview */}
                          <p className="line-clamp-2 text-[11px] text-slate-400 dark:text-slate-500">
                            {t.promptText.slice(0, 120)}…
                          </p>

                          {/* Hover actions */}
                          <div className="absolute right-3 top-3 hidden gap-1 group-hover:flex">
                            <button
                              onClick={() => openEditTmpl(t)}
                              className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/90 text-slate-400 shadow-sm hover:text-slate-600 dark:bg-slate-700 dark:hover:text-slate-200"
                              title="Edit template"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => deleteTmpl(t)}
                              className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/90 text-slate-400 shadow-sm hover:text-red-500 dark:bg-slate-700"
                              title="Deactivate template"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              ) : (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
                  <GripVertical className="h-10 w-10 text-slate-300 dark:text-slate-600" />
                  <p className="text-slate-500 dark:text-slate-400">
                    {templateSearch ? `No templates matching "${templateSearch}"` : 'No templates in this category yet'}
                  </p>
                  {!templateSearch && (
                    <Button size="sm" onClick={openCreateTmpl}>
                      <Plus className="h-4 w-4 mr-1.5" />
                      Add First Template
                    </Button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
              <BookOpen className="h-12 w-12 text-slate-300 dark:text-slate-600" />
              <p className="text-lg font-medium text-slate-500 dark:text-slate-400">Select a category</p>
              <p className="max-w-xs text-sm text-slate-400 dark:text-slate-500">
                Choose a category from the left panel to view and manage its prompt templates
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ─── Category Modal ────────────────────────── */}
      <Dialog open={catModalOpen} onOpenChange={setCatModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCat ? 'Edit Category' : 'New Category'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {catError && (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-400">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {catError}
              </div>
            )}

            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Name *</label>
              <Input
                value={catForm.name}
                onChange={(e) => {
                  const name = e.target.value;
                  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                  setCatForm((f) => ({ ...f, name, ...(editingCat ? {} : { slug }) }));
                }}
                placeholder="Employment Law"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Slug *</label>
              <Input
                value={catForm.slug}
                onChange={(e) => setCatForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="employment-law"
              />
              <p className="text-xs text-slate-400">Lowercase letters, numbers, and hyphens only</p>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Icon</label>
              <Input
                value={catForm.icon}
                onChange={(e) => setCatForm((f) => ({ ...f, icon: e.target.value }))}
                placeholder="briefcase"
              />
              <p className="text-xs text-slate-400">Lucide icon name (e.g., briefcase, home, heart-pulse)</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Color</label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setCatForm((f) => ({ ...f, color: opt.value }))}
                    className={cn(
                      'flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-all',
                      catForm.color === opt.value
                        ? 'border-indigo-400 bg-indigo-50 text-indigo-700 dark:border-indigo-600 dark:bg-indigo-950/30 dark:text-indigo-300'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:text-slate-400',
                    )}
                  >
                    <span className={cn('h-3 w-3 rounded-full', opt.cls)} />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCatModalOpen(false)} disabled={catSaving}>
              Cancel
            </Button>
            <Button onClick={saveCat} disabled={catSaving}>
              {catSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingCat ? 'Save Changes' : 'Create Category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Template Modal ────────────────────────── */}
      <Dialog open={tmplModalOpen} onOpenChange={setTmplModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingTmpl ? 'Edit Template' : 'New Prompt Template'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {tmplError && (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-400">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {tmplError}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Title *</label>
                <Input
                  value={tmplForm.title}
                  onChange={(e) => setTmplForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Employment Offer Letter"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Sort Order</label>
                <Input
                  type="number"
                  value={tmplForm.sortOrder}
                  onChange={(e) => setTmplForm((f) => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Description</label>
              <Input
                value={tmplForm.description}
                onChange={(e) => setTmplForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Brief description shown to users in the template card"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Prompt Text *
              </label>
              <textarea
                value={tmplForm.promptText}
                onChange={(e) => setTmplForm((f) => ({ ...f, promptText: e.target.value }))}
                placeholder="Create a comprehensive employment offer letter with the following sections..."
                className="block min-h-[200px] w-full resize-y rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
                aria-label="Prompt text"
              />
              <p className="text-right text-xs text-slate-400">{tmplForm.promptText.length}/5000</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTmplModalOpen(false)} disabled={tmplSaving}>
              Cancel
            </Button>
            <Button onClick={saveTmpl} disabled={tmplSaving}>
              {tmplSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingTmpl ? 'Save Changes' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PromptTemplates;
